import React, { useContext, useEffect, useState } from 'react';
import styles from './farmCreationFlow.module.scss';
import CardWithStickyHeader, {
  ButtonProps,
} from 'shared/components/CardWithStickyHeader';
import { ReactComponent as AddIcon } from 'assets/icons/add_enclosed.svg';
import { ArrowRightOutlined } from '@ant-design/icons';
import { ButtonType } from 'enums/ButtonEnums';
import {
  actionButtons,
  farmStagesTitles,
} from 'constants/FarmboxConstants/stage0.constants';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Category1TypeFields, Category1Types } from 'enums/category1Enums';
import FertilisersFields from './FertilisersFields';
import {
  FarmboxRawMaterials,
  FarmboxRawMaterialsData,
} from 'models/FarmboxRawMaterials/farmBoxRawMaterials.model';
import FarmboxRawMaterialsService from 'services/Farmbox/RawMaterials/farmboxRawMaterials.service';
import { CategoryOptions } from 'shared/components/CreateProductModal/ProductModalForm';
import { useParams, useSearchParams } from 'react-router-dom';
import useRedirect from 'shared/utils/useRedirect';
import useFarmboxRedirections from 'shared/utils/useFarmboxRedirections';
import { resetOriginsToEmptyArray } from 'shared/utils/resetOriginsToEmptyArray';
import isProductDisabled from 'shared/utils/isProductDisabled';
import FarmboxService from 'services/Farmbox/farmbox.service';
import { Commodity } from 'models/Farmbox/commodity.model';
import { adePage, commodity, transportation } from 'constants/constants';
import { HelpDeskContext } from 'context/HelpDeskContext';
import Stage1AndStage2ProductService from 'services/ProductsService/stage1AndStage2Product.service';

const FarmRawMaterials = () => {
  const { control, handleSubmit, reset } = useFormContext();
  const { setIsHelpDeskOpen } = useContext(HelpDeskContext);
  const { redirectToRespectiveScopeTab } = useRedirect();
  const { redirectToRespectiveCommodityStage, redirectToFarm } =
    useFarmboxRedirections();
  const { ADD_PACKAGING_MATERIAL, ADD_FERTILISER, NEXT } = actionButtons;
  const { TEXT, PRIMARY } = ButtonType;
  const { id: commodityId, status } = useParams();
  const [searchParams] = useSearchParams();
  const queryParams = Object.fromEntries(searchParams.entries());
  const { RAW_MATERIALS } = farmStagesTitles;
  const {
    fetchMetaData,
    rawMaterialFertilisersMeta,
    updateRawMaterials,
    getRawMaterials,
    loading,
    buttonLoading,
  } = FarmboxRawMaterialsService();
  const { fetchPackagingMaterials, rawMateialPackagingMeta } =
    Stage1AndStage2ProductService();
  const { getCommodity } = FarmboxService();
  const [commodityData, setCommodityData] = useState<Commodity>();

  useEffect(() => {
    fetchResponseData();
  }, []);

  const fetchResponseData = async () => {
    await fetchMetaData();
    const rawMaterials = await getRawMaterials(Number(commodityId));
    await fetchPackagingMaterials();
    const specificCommodityData = await getCommodity(Number(commodityId));
    if (specificCommodityData) setCommodityData(specificCommodityData);
    // for reset the default values of form
    const areAllArraysEmpty =
      !rawMaterials?.fertilizers?.[0]?.activityLabel &&
      !rawMaterials?.packagings?.[0]?.activityLabel;

    const defaultValues = !areAllArraysEmpty
      ? {
          ...rawMaterials,
          fertilizers: rawMaterials?.fertilizers?.length
            ? rawMaterials?.fertilizers
            : [new FarmboxRawMaterials()],
          packagings: rawMaterials?.packagings?.length
            ? rawMaterials?.packagings
            : [],
        }
      : new FarmboxRawMaterialsData();
    reset(defaultValues);
  };
  const { isFrom, page } = queryParams;
  const isDisabled = isProductDisabled(status);

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: Category1TypeFields.FERTILISERS, // Name of the array in the form data
  });

  const {
    fields: packagingMaterialFields,
    append: appendPackagingMaterial,
    remove: removePackagingMaterial,
  } = useFieldArray({
    control: control,
    name: Category1TypeFields.PACKAGING_MATERIALS,
  });

  const handleAddFertiliser = () =>
    append(new FarmboxRawMaterials(), { shouldFocus: false });
  const handleAddPackagingMaterial = () =>
    appendPackagingMaterial(new FarmboxRawMaterials(), { shouldFocus: false });

  const handleFormSubmit = async (data: FarmboxRawMaterialsData) => {
    const isPackagingMaterials = data?.packagings?.[0]?.activityLabel;
    data?.fertilizers?.forEach(resetOriginsToEmptyArray);
    data?.packagings?.forEach(resetOriginsToEmptyArray);
    const areAllArraysEmpty =
      !data?.fertilizers?.[0]?.activityLabel && !isPackagingMaterials;
    if (!isPackagingMaterials) {
      data.packagings = [];
    }
    if (!areAllArraysEmpty && !isDisabled) {
      const success = await updateRawMaterials(Number(commodityId), data);
      if (success)
        redirectToRespectiveCommodityStage(
          Number(commodityId),
          'transportation',
          status,
          queryParams
        );
    } else {
      redirectToRespectiveCommodityStage(
        Number(commodityId),
        transportation,
        status,
        queryParams
      );
    }
  };
  const handleBackNavigation = () => {
    setIsHelpDeskOpen(false);
    if (isFrom === adePage) {
      commodityData?.locationId &&
        redirectToRespectiveScopeTab(
          commodityData?.locationId,
          commodity,
          String(commodityData?.harvestYear)
        );
    } else {
      redirectToFarm(isFrom, { page: Number(page) });
    }
  };

  const actionButtonsList: ButtonProps[] = [
    {
      icon: <AddIcon />,
      title: ADD_FERTILISER,
      clickCallback: handleAddFertiliser,
      type: TEXT,
      isAdd: true,
    },
    {
      icon: <AddIcon />,
      title: ADD_PACKAGING_MATERIAL,
      clickCallback: handleAddPackagingMaterial,
      type: TEXT,
      isAdd: true,
    },
    {
      icon: <ArrowRightOutlined />,
      title: NEXT,
      clickCallback: handleSubmit(handleFormSubmit),
      type: PRIMARY,
      isAdd: false,
      loading: buttonLoading,
    },
  ];

  const formElement = (
    <div className={styles.farmElementContainer}>
      <div className={styles.headingContainer} data-testid="fertilizer-heading">
        Fertiliser / Pesticide Inputs
      </div>
      {fields?.map((field, index) => (
        <div
          key={field.id}
          className={styles.fieldsContainer}
          data-testid={`fertilizer-field-${index}`}>
          <FertilisersFields
            index={index}
            type={Category1Types.FERTILISERS}
            remove={remove}
            nameOptions={rawMaterialFertilisersMeta as CategoryOptions[]}
          />
        </div>
      ))}
      {packagingMaterialFields?.length ? (
        <div>
          <div className={styles.divider}>
            <hr />
          </div>
          <div
            className={styles.headingContainer}
            data-testid="packaging-heading">
            Packaging Materials
          </div>
        </div>
      ) : null}
      {packagingMaterialFields?.map((field, index) => (
        <div
          key={field.id}
          className={styles.fieldsContainer}
          data-testid={`packaging-field-${index}`}>
          <FertilisersFields
            index={index}
            type={Category1Types.PACKAGING_MATERIALS}
            remove={removePackagingMaterial}
            nameOptions={rawMateialPackagingMeta as CategoryOptions[]}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.farmRawMaterialsContainer}>
      <CardWithStickyHeader
        title={RAW_MATERIALS}
        children={formElement}
        actionButtons={actionButtonsList}
        loading={loading}
        backCallback={handleBackNavigation}
        data-testid="farm-raw-materials"
      />
    </div>
  );
};

export default FarmRawMaterials;
